<template>
  <div id="ajax-page" class="ajax-page-content">
    <div class="ajax-page-wrapper">
      <div class="ajax-page-nav">
        <div class="nav-item ajax-page-prev-next">
          <a class="ajax-page-load" href="#" @click.prevent="methods.onPrev()">
            <i class="lnr lnr-chevron-left"></i>
          </a>
          <a class="ajax-page-load" href="#" @click.prevent="methods.onNext()">
            <i class="lnr lnr-chevron-right"></i>
          </a>
        </div>
        <div class="nav-item ajax-page-close-button">
          <a id="ajax-page-close-button" href="#" v-on:click.prevent="methods.onClose()"
            ><i class="lnr lnr-cross"></i
          ></a>
        </div>
      </div>

      <div class="ajax-page-title">
        <h1 v-if="isReady">{{ data.name }}</h1>
        <SkeletonText width="150px" height="32px" v-else />
      </div>

      <div class="row">
        <div class="col-sm-8 col-md-8 portfolio-block">
          <div
            class="owl-carousel portfolio-page-carousel owl-loaded owl-drag"
            v-if="willShow.img >= 1"
            :style="{ opacity: willShow.img === 2 ? 1 : 0 }"
          >
            <div class="item" v-for="(item, i) in data.screenshoots" :key="i">
              <img :src="item" alt="" />
            </div>
          </div>
          <div
            class="owl-carousel portfolio-page-carousel owl-loaded owl-drag"
            v-if="willShow.img <= 1"
            style="position: absolute; top: 0; padding-top: 30px"
          >
            <SkeletonText width="690px" height="370px" />
          </div>
        </div>

        <div class="col-sm-4 col-md-4 portfolio-block">
          <!-- Project Description -->
          <div class="project-description">
            <div class="block-title">
              <h3>Description</h3>
            </div>
            <ul class="project-general-info">
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else><i class="fa fa-building"></i> {{ data.company }}</p>
              </li>
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else>
                  <i class="fa fa-globe"></i>
                  <a :href="data.demoUrl" target="_blank" v-if="data.demoUrl">
                    {{ data.demoUrl }}
                  </a>
                  <a href="#" v-else @click.prevent> Not available today </a>
                </p>
              </li>
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else>
                  <i class="fa fa-calendar"></i>
                  {{ moment(new Date(data.createdAt)).format('DD MMMM, YYYY') }}
                </p>
              </li>
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else><i class="fa fa-tags"></i> {{ data.type }}</p>
              </li>
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else><i class="fa fa-map-marker-alt"></i> {{ data.location }}</p>
              </li>
              <li>
                <p v-if="!isReady">
                  <SkeletonText width="150px" height="20px" style="margin-top: -4px" />
                </p>
                <p v-else><i class="fa fa-info-circle"></i> {{ data.status }}</p>
              </li>
            </ul>

            <p class="text-justify" v-if="isReady">
              {{ data.description }}
            </p>
            <p class="text-justify" v-else>
              <SkeletonText width="100%" height="200px" />
            </p>
            <!-- /Project Description -->

            <!-- Technology -->
            <div class="tags-block">
              <div class="block-title">
                <h3>Technology</h3>
              </div>
              <ul class="tags" v-if="isReady">
                <li v-for="(item, i) in data.technologies" :key="i">
                  <a>{{ item }}</a>
                </li>
              </ul>
              <ul class="tags" v-if="!isReady">
                <li>
                  <SkeletonText width="40px" height="25px" style="margin-right: 7px" />
                </li>
                <li>
                  <SkeletonText width="60px" height="25px" style="margin-right: 7px" />
                </li>
                <li>
                  <SkeletonText width="50px" height="25px" style="margin-right: 7px" />
                </li>
              </ul>
            </div>
            <!-- /Technology -->

            <!-- Share Buttons -->
            <div class="btn-group share-buttons">
              <div class="block-title">
                <h3>Share</h3>
              </div>
              <a href="#" target="_blank" class="btn" @click.prevent="methods.onShare('facebook')">
                <i class="fab fa-facebook-f"></i>
              </a>
              <a href="#" target="_blank" class="btn" @click.prevent="methods.onShare('twitter')">
                <i class="fab fa-twitter"></i>
              </a>
            </div>
            <!-- /Share Buttons -->
          </div>
          <!-- Project Description -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {inject, onMounted, reactive, ref, watch} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import DATA from '../data'
import SkeletonText from './SkeletonText'
import moment from 'moment'

export default {
  name: 'PageAjax',
  components: {
    SkeletonText
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const isReady = ref(false)
    const pageAjaxLoaded = inject('pageAjaxLoaded')
    const pageAjaxCounter = inject('pageAjaxCounter')
    const data = ref()
    const prevData = ref()
    const nextData = ref()
    watch(pageAjaxLoaded, (state) => {
      if (state.ready) {
        setTimeout(() => {
          methods.onReloadDataItems()
          methods.init()
        }, 400)
      }
    })

    const willShow = reactive({
      img: 0,
      skeleton: true
    })

    const methods = {
      onReloadDataItems: () => {
        DATA.portfolio.items.map((item, itemI) => {
          if (item.id === route.query.id) {
            if (DATA.portfolio.items[itemI - 1]) {
              prevData.value = DATA.portfolio.items[itemI - 1]
            } else {
              prevData.value = DATA.portfolio.items[DATA.portfolio.items.length - 1]
            }

            data.value = item

            if (DATA.portfolio.items[itemI + 1]) {
              nextData.value = DATA.portfolio.items[itemI + 1]
            } else {
              nextData.value = DATA.portfolio.items[0]
            }
          }
        })
      },
      onPrev: () => {
        if (prevData.value) {
          pageAjaxCounter.value = 0
          pageAjaxLoaded.destroy = false
          pageAjaxLoaded.enabled = true
          router.push({ name: 'Portfolio', query: { id: prevData.value.id } })
          setTimeout(() => {
            pageAjaxLoaded.ready = true
            methods.onReloadDataItems()
            $('.portfolio-page-carousel').owlCarousel('destroy')
            methods.init()
          }, 100)
        } else {
          //
        }
      },
      onNext: () => {
        if (nextData.value) {
          pageAjaxCounter.value = 0
          pageAjaxLoaded.destroy = false
          pageAjaxLoaded.enabled = true
          router.push({ name: 'Portfolio', query: { id: nextData.value.id } })
          setTimeout(() => {
            pageAjaxLoaded.ready = true
            methods.onReloadDataItems()
            $('.portfolio-page-carousel').owlCarousel('destroy')
            methods.init()
          }, 100)
        } else {
          //
        }
      },
      onClose: () => {
        pageAjaxLoaded.ready = false
        pageAjaxLoaded.enabled = false
        setTimeout(() => {
          pageAjaxCounter.value = 0
          isReady.value = false
          willShow.img = 0
          pageAjaxLoaded.destroy = true
        }, 1000)
        router.push({ name: 'Portfolio' })
      },
      init: () => {
        pageAjaxCounter.value++
        if (pageAjaxCounter.value === 1) {
          setTimeout(() => {
            isReady.value = true

            let imageLoaded = 0
            data.value.screenshoots.forEach((item) => {
              const img = new Image()
              img.src = item

              img.onload = () => {
                imageLoaded++

                if (imageLoaded === data.value.screenshoots.length) {
                  willShow.img = 1
                  setTimeout(() => {
                    $('.portfolio-page-carousel').imagesLoaded(function () {
                      $('.portfolio-page-carousel').owlCarousel({
                        smartSpeed: 1200,
                        items: 1,
                        loop: false,
                        dots: true,
                        nav: true,
                        navText: false,
                        margin: 10,
                        autoHeight: true
                      })
                    })
                    setTimeout(() => {
                      willShow.img = 2
                    }, 500)
                  }, 100)
                }
              }
            })
          }, 3000)
        }
      },
      onShare: (socmed) => {
        const pageUrl = encodeURIComponent(document.URL)
        if (socmed === 'facebook') {
          const url = `https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`
          window.open(url, '_blank')
        } else if (socmed === 'twitter') {
          const url = `https://twitter.com/intent/tweet?url=${pageUrl}`
          window.open(url, '_blank')
        }
      }
    }

    onMounted(() => {
      methods.onReloadDataItems()
      methods.init()
    })

    return { willShow, moment, methods, isReady, data, route }
  }
}
</script>
